import React, { useState, useEffect } from "react";
import { ChevronDownIcon, X } from "lucide-react";
// Import original lugarService sólo si se requiere; para el select usaremos el endpoint de eventService
// porque devuelve directamente las opciones usadas también en creación de eventos.
// import lugarService from '../../services/lugarService';
import InlineSpinner from "../ui/InlineSpinner";
import clsx from "clsx";

/* ModalUsuario
   Props:
   - open
   - onClose
   - usuario (obj | null)
   - onGuardar(datos)
   - isLoading (bool)
*/
export default function ModalUsuario({
  open,
  onClose,
  usuario = null,
  onGuardar,
  isLoading = false,
}) {
  const esEdicion = Boolean(usuario?.id);
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    password: "",
    rol: "admin",
    contacto: "",
    direccion: "",
    lugar_id: "",
  });
  const [errores, setErrores] = useState({});
  const [lugares, setLugares] = useState([]);
  const [cargandoLugares, setCargandoLugares] = useState(false);

  useEffect(() => {
    if (open && form.rol === "lugar") {
      cargarLugares();
    }
  }, [open, form.rol]);

  useEffect(() => {
    if (usuario && open) {
      setForm({
        nombre: usuario.nombre || "",
        email: usuario.email || "",
        password: "", // no se muestra en edición
        rol: Array.isArray(usuario.rol)
          ? usuario.rol[0]
          : usuario.rol || "admin",
        contacto: usuario.contacto || "",
        direccion: usuario.direccion || "",
        lugar_id: usuario.lugar_id || usuario.lugar?.id || "",
      });
    } else if (open) {
      setForm({
        nombre: "",
        email: "",
        password: "",
        rol: "admin",
        contacto: "",
        direccion: "",
        lugar_id: "",
      });
      setErrores({});
    }
  }, [usuario, open]);

  const cargarLugares = async () => {
    try {
      setCargandoLugares(true);
      // Carga dinámica para no incrementar el bundle inicial
      const eventService = (await import("../../services/eventService"))
        .default;
      const res = await eventService.getLugares();
      if (res.success) {
        // res.data puede ser: [ ... ]  ó  { lugares: [...] }
        const lista = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.lugares)
          ? res.data.lugares
          : [];
        setLugares(lista);
      } else {
        setLugares([]);
      }
    } catch (e) {
      setLugares([]);
    } finally {
      setCargandoLugares(false);
    }
  };

  const validar = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = "Nombre requerido";
    if (!form.email.trim()) e.email = "Email requerido";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      e.email = "Email inválido";
    if (!esEdicion && form.rol !== "lugar" && !form.password.trim())
      e.password = "Contraseña requerida";
    if (form.rol === "lugar" && !form.lugar_id)
      e.lugar_id = "Selecciona un lugar";
    if (!form.rol) e.rol = "Rol requerido";
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validar()) return;
    const payload = {
      nombre: form.nombre.trim(),
      email: form.email.trim(),
      rol: form.rol, // aunque API espera array, adaptamos en página si es necesario
      contacto: form.contacto.trim(),
      direccion: form.direccion.trim(),
      ...(form.rol === "lugar" ? { lugar_id: form.lugar_id } : {}),
      ...(!esEdicion && form.rol !== "lugar"
        ? { password: form.password }
        : {}),
    };
    onGuardar(payload);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-white dark:bg-[#1e1e1e] rounded-[1.5rem] sm:rounded-[2rem] shadow-2xl border border-gray-100 dark:border-gray-800 transition-all transform scale-100 max-h-[90vh] overflow-y-auto">
        
        {/* Header decoration */}
        <div className="absolute top-0 left-0 w-full h-2 bg-[#206a73]" />
        
        <div className="p-5 sm:p-8">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all"
          >
            <X size={20} />
          </button>
          
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              {esEdicion ? "Editar Perfil de Usuario" : "Registrar Nuevo Usuario"}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 font-medium mt-1">
              {esEdicion
                ? "Actualiza la información y permisos de acceso."
                : "Define las credenciales y el rol para el nuevo integrante."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 relative">
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="block text-sm font-bold mb-1.5 text-gray-700 dark:text-gray-300 ml-1">Nombre Completo</label>
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Ej: Juan Pérez"
                  className={clsx(
                    "block w-full rounded-2xl border-2 bg-gray-50/50 dark:bg-[#252525] px-4 py-3 text-sm dark:text-white text-gray-700 transition-all",
                    "placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#206a73]/20 focus:border-[#206a73]",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    errores.nombre
                      ? "border-red-500"
                      : "border-gray-100 dark:border-gray-800"
                  )}
                />
                {errores.nombre && (
                  <p className="text-xs text-red-500 mt-1.5 ml-1 font-medium">{errores.nombre}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold mb-1.5 text-gray-700 dark:text-gray-300 ml-1">Correo Electrónico</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  disabled={isLoading || esEdicion}
                  placeholder="correo@ejemplo.com"
                  className={clsx(
                    "block w-full rounded-2xl border-2 bg-gray-50/50 dark:bg-[#252525] px-4 py-3 text-sm dark:text-white text-gray-700 transition-all",
                    "placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#206a73]/20 focus:border-[#206a73]",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    errores.email
                      ? "border-red-500"
                      : "border-gray-100 dark:border-gray-800"
                  )}
                />
                {errores.email && (
                  <p className="text-xs text-red-500 mt-1.5 ml-1 font-medium">{errores.email}</p>
                )}
              </div>
            </div>

            {form.rol !== "lugar" && !esEdicion && (
              <div>
                <label className="block text-sm font-bold mb-1.5 text-gray-700 dark:text-gray-300 ml-1">Contraseña</label>
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Mínimo 6 caracteres"
                  className={clsx(
                    "block w-full rounded-2xl border-2 bg-gray-50/50 dark:bg-[#252525] px-4 py-3 text-sm dark:text-white text-gray-700 transition-all",
                    "placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#206a73]/20 focus:border-[#206a73]",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    errores.password
                      ? "border-red-500"
                      : "border-gray-100 dark:border-gray-800"
                  )}
                />
                {errores.password && (
                  <p className="text-xs text-red-500 mt-1.5 ml-1 font-medium">{errores.password}</p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-bold mb-1.5 text-gray-700 dark:text-gray-300 ml-1">Rol del Sistema</label>
                <div className="relative">
                  <select
                    name="rol"
                    value={form.rol}
                    onChange={handleChange}
                    disabled={isLoading}
                    className={clsx(
                      "block w-full appearance-none rounded-2xl border-2 bg-gray-50/50 dark:bg-[#252525] px-4 py-3 text-sm dark:text-white text-gray-700 transition-all cursor-pointer",
                      "focus:outline-none focus:ring-2 focus:ring-[#206a73]/20 focus:border-[#206a73]",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                      errores.rol
                        ? "border-red-500"
                        : "border-gray-100 dark:border-gray-800"
                    )}
                  >
                    <option value="admin" className="bg-white dark:bg-[#1e1e1e]">Administrador</option>
                    <option value="operador" className="bg-white dark:bg-[#1e1e1e]">Operador</option>
                    <option value="lugar" className="bg-white dark:bg-[#1e1e1e]">Lugar</option>
                  </select>
                  <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold mb-1.5 text-gray-700 dark:text-gray-300 ml-1">Contacto</label>
                <input
                  name="contacto"
                  value={form.contacto}
                  onChange={handleChange}
                  disabled={isLoading}
                  placeholder="Teléfono"
                  className="block w-full rounded-2xl border-2 bg-gray-50/50 dark:bg-[#252525] border-gray-100 dark:border-gray-800 px-4 py-3 text-sm dark:text-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#206a73]/20 focus:border-[#206a73] transition-all placeholder:text-gray-400"
                />
              </div>
            </div>

            {form.rol === "lugar" && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                <label className="block text-sm font-bold mb-1.5 text-gray-700 dark:text-gray-300 ml-1">Lugar Asignado</label>
                {cargandoLugares ? (
                  <div className="px-4 py-3 text-sm text-gray-500 bg-gray-50 dark:bg-[#252525] rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-800">
                    Buscando lugares disponibles...
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      name="lugar_id"
                      value={form.lugar_id}
                      onChange={handleChange}
                      disabled={isLoading}
                      className={clsx(
                        "block w-full appearance-none rounded-2xl border-2 bg-gray-50/50 dark:bg-[#252525] px-4 py-3 text-sm dark:text-white text-gray-700 transition-all cursor-pointer",
                        "focus:outline-none focus:ring-2 focus:ring-[#206a73]/20 focus:border-[#206a73]",
                        "disabled:opacity-50 disabled:cursor-not-allowed",
                        errores.lugar_id
                          ? "border-red-500"
                          : "border-gray-100 dark:border-gray-800"
                      )}
                    >
                      <option value="" className="bg-white dark:bg-[#1e1e1e]">Selecciona un lugar</option>
                      {Array.isArray(lugares) &&
                        lugares.map((l) => (
                          <option key={l.id} value={l.id} className="bg-white dark:bg-[#1e1e1e]">
                            {l.nombre}
                          </option>
                        ))}
                    </select>
                    <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                )}
                {errores.lugar_id && (
                  <p className="text-xs text-red-500 mt-1.5 ml-1 font-medium">{errores.lugar_id}</p>
                )}
              </div>
            )}

            <div className="pt-4 flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-6 py-3 rounded-2xl border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all active:scale-95"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-8 py-3 rounded-2xl bg-[#206a73] text-white text-sm font-bold hover:bg-[#155059] shadow-lg shadow-[#206a73]/20 disabled:opacity-60 transition-all active:scale-95 flex items-center justify-center gap-2 min-w-[140px]"
              >
                {isLoading && <InlineSpinner size="sm" />}
                {isLoading
                  ? "Procesando..."
                  : esEdicion
                  ? "Actualizar"
                  : "Registrar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
